import { useState, type ReactNode } from 'react';
import { LogOut } from 'lucide-react';
import { NordcadLogo } from './NordcadLogo';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { useAuth } from '../lib/queries';
import { logout } from '../lib/auth';
import { hideDemoBadges } from '../lib/flags';

export function AppShell({ children }: { children: ReactNode }) {
  const { data: auth } = useAuth();
  const [menu, setMenu] = useState(false);
  const name = auth?.user?.name ?? 'Bruger';
  const first = name.split(/\s+/)[0];
  const initials = name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="flex min-h-full flex-col">
      <header className="sticky top-0 z-20 border-b border-brand-primary/10 bg-brand-cream/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between gap-6 px-6">
          <div className="flex items-center gap-3">
            <NordcadLogo />
            <span className="hidden border-l border-brand-primary/15 pl-3 text-xs uppercase tracking-[0.18em] text-brand-primary/60 sm:inline">
              Kontrolcenter
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-brand-dark/60 sm:inline">
              Hej, <span className="font-medium text-brand-dark">{first}</span>
            </span>
            {auth?.devBypass && !hideDemoBadges() && (
              <span className="rounded-full bg-brand-amber-light px-2 py-1 text-[10px] uppercase tracking-wider text-brand-amber">Dev</span>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenu((m) => !m)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-primary text-xs font-semibold text-white"
                aria-label="Brugermenu"
              >
                {initials}
              </button>
              {menu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
                  <div className="absolute right-0 z-20 mt-2 w-48 rounded-brand border border-brand-primary/10 bg-white p-1 shadow-xl">
                    {auth?.user?.email && <div className="px-3 py-2 text-xs text-brand-dark/50">{auth.user.email}</div>}
                    <button
                      type="button"
                      onClick={() => void logout()}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-dark transition-colors hover:bg-brand-primary/5"
                    >
                      <LogOut size={15} /> Log ud
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-6">
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>

      <footer className="border-t border-brand-primary/10 py-4 text-center text-xs text-brand-dark/40">
        Nordcad Kontrolcenter · data fra SuperOffice, Google Analytics &amp; ActiveCampaign
      </footer>
    </div>
  );
}
